import { REGULAR_OPEN_MINUTES, regularCloseMinutesFor, sessionClock } from './sessionFilter'

export interface Period {
  multiplier: number
  timespan: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'
  label: string
  /** Full display name for menus */
  fullLabel: string
  /** Milliseconds per bar */
  ms: number
}

/** True for one-day bars (1D). Day-scoped actions like "Drill into this day" need this. */
export function isDailyPeriod(period: Period): boolean {
  return period.timespan === 'day' && period.multiplier === 1
}

/** True for intraday timeframes (minute / hour) vs daily-and-up (day/week/…). */
export function isIntradayPeriod(period: Period): boolean {
  return period.timespan === 'minute' || period.timespan === 'hour'
}

function p(
  multiplier: number,
  timespan: Period['timespan'],
  label: string,
  fullLabel: string,
): Period {
  const msPerUnit: Record<Period['timespan'], number> = {
    minute: 60_000,
    hour: 3_600_000,
    day: 86_400_000,
    week: 604_800_000,
    month: 2_592_000_000,
    year: 31_536_000_000,
  }
  return { multiplier, timespan, label, fullLabel, ms: multiplier * msPerUnit[timespan] }
}

export const PERIODS = {
  '1m': p(1, 'minute', '1m', '1 Minute'),
  '2m': p(2, 'minute', '2m', '2 Minutes'),
  '3m': p(3, 'minute', '3m', '3 Minutes'),
  '5m': p(5, 'minute', '5m', '5 Minutes'),
  '10m': p(10, 'minute', '10m', '10 Minutes'),
  '15m': p(15, 'minute', '15m', '15 Minutes'),
  '30m': p(30, 'minute', '30m', '30 Minutes'),
  '1H': p(1, 'hour', '1H', '1 Hour'),
  '4H': p(4, 'hour', '4H', '4 Hours'),
  '1D': p(1, 'day', '1D', '1 Day'),
  '1W': p(1, 'week', '1W', '1 Week'),
  // Monthly is the top of the ladder. There is deliberately no yearly period:
  // the backend Resolution enum stops at MONTHLY ("1M"), so `periodToResolution`
  // mapped 'year' onto "1M" and a user picking 1Y silently received MONTHLY
  // bars while the chart still treated each one as spanning a year. With ~15
  // years of history a yearly timeframe would show a handful of bars anyway.
  '1M': p(1, 'month', '1M', '1 Month'),
} as const satisfies Record<string, Period>

export type PeriodKey = keyof typeof PERIODS

/**
 * Sentinel for "all time" lookbacks. Callers must handle this specially —
 * an unbounded request would be invalid; the scroll-back loop in DataLayer
 * fetches a bounded most-recent page and pages older bars on demand.
 *
 * Spec: docs/superpowers/specs/2026-05-04-chart-lookback-and-routing-design.md
 */
export const ALL_TIME = Number.POSITIVE_INFINITY

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Initial fetch size, in bars. The chart requests this many bars on first
 * paint and on every scroll-back page. Sized to comfortably exceed
 * klinecharts' default visible bar count (~150–300) so the user gets a
 * scroll-left buffer before the next page request fires.
 *
 * Spec: docs/superpowers/specs/2026-05-05-chart-fetch-volume-redesign-design.md
 */
export const INITIAL_PAGE_BARS = 500

// TODO(chris, 2026-05-05) — confirm meaning of LOOKBACK_BY_PERIOD: it is
// now the scroll-back cap (max distance from `Date.now()` the user can
// scroll back), not the initial fetch size. See the redesign doc for the
// full rationale and current behavior:
// docs/superpowers/specs/2026-05-05-chart-fetch-volume-redesign-design.md
/**
 * Soft scroll-back cap per period — the maximum distance, measured from
 * `Date.now()`, that the user can scroll back in time on the chart.
 *
 * Values mirror the original Task 1 spec table verbatim, but the meaning
 * has shifted: this is no longer the initial fetch size (see
 * `INITIAL_PAGE_BARS`). Scroll-back stops at the closer-to-now of (a) this
 * cap and (b) the per-ticker hard floor from `etl_latest_dates`.
 *
 * `ALL_TIME` (= `Infinity`) means "no soft cap; only the per-ticker floor
 * stops further scroll-back." The DataLayer's runtime "empty response =
 * floorReached" detection is the final fallback when neither cap nor
 * per-ticker floor is known.
 *
 * **Unit: calendar days, not trading days.** The values are multiples of
 * `DAY_MS` (24h ms-spans), so `45 * DAY_MS` covers 45 wall-clock days from
 * now — roughly 32 trading days after weekends, plus or minus the holiday
 * density of the window. This was a deliberate choice: it makes the cap
 * stable across DST transitions and easy to reason about in date math, at
 * the cost of slightly fewer trading days during holiday-heavy stretches.
 * For the actual per-ticker coverage (e.g. an IPO last week has ~3 days of
 * data regardless of this cap), see `v_etl_latest_dates` exposed via
 * `GET /v2/symbol/{ticker}/floor`.
 *
 * Source: "Task 1 — Lookback Windows by Interval" + "Task 5a — Market Hours
 * vs Extended Hours" in
 * docs/superpowers/specs/2026-05-04-chart-lookback-and-routing-design.md.
 */
export const LOOKBACK_BY_PERIOD: Record<PeriodKey, number> = {
  '1m': 45 * DAY_MS,
  '2m': 60 * DAY_MS,
  '3m': 60 * DAY_MS,
  '5m': 90 * DAY_MS,
  '10m': 120 * DAY_MS,
  '15m': 365 * DAY_MS,
  '30m': 365 * DAY_MS,
  '1H': ALL_TIME,
  '4H': ALL_TIME,
  '1D': ALL_TIME,
  '1W': ALL_TIME,
  '1M': ALL_TIME,
}

/**
 * Reverse-lookup the `PeriodKey` from a `Period` object. Returns `null` when
 * the input doesn't structurally match any entry in `PERIODS` (shouldn't
 * happen in normal flow). Used by both `DataLayer` (for `LOOKBACK_BY_PERIOD`
 * lookup on scroll-back) and `ChartManager` (for `MultiPeriodCache` keys).
 */
export function periodKeyOf(period: Period): PeriodKey | null {
  const entry = (Object.entries(PERIODS) as [PeriodKey, Period][]).find(([, p]) => p === period)
  if (entry) return entry[0]
  const matched = (Object.entries(PERIODS) as [PeriodKey, Period][]).find(
    ([, p]) => p.multiplier === period.multiplier && p.timespan === period.timespan,
  )
  return matched?.[0] ?? null
}

/**
 * Align a timestamp to a period boundary, matching the backend's pandas
 * resample boundaries so realtime bars merge cleanly with historical bars.
 *
 * Backend behavior (UTC, naïve):
 *   - Daily   → midnight UTC of the bar's day
 *   - Weekly  → Monday midnight UTC (rule `1W-MON`, label="left")
 *   - Monthly → first day of month at midnight UTC (`x.replace(day=1)`)
 *   - Yearly  → first day of year at midnight UTC
 *
 * Why not `Math.floor(ts / period.ms) * period.ms`:
 *   - `period.ms` for `month` is a 30-day approximation — drifts by 1–3 days
 *     against actual calendar months.
 *   - `period.ms` for `year` is 365 days — drifts by 1 day per leap year.
 *   - `period.ms` for `week` is 7 days, but Unix epoch (1970-01-01) was a
 *     Thursday — flooring aligns to Thursdays, not Mondays.
 * Calendar buckets are not multiples of a fixed millisecond span; they need
 * `Date` math.
 *
 * `regularSession` (charts showing the regular session only): bars that do not
 * tile from the 09:30 open on clock boundaries (1H, 4H) start at the open
 * instead, as the server's `market_session=mkt` bars do. See
 * {@link regularSessionBucket}.
 */
export function alignTimestamp(
  ts: number,
  period: Period,
  regularSession?: RegularSessionBuckets | null,
): number {
  if (regularSession) {
    const anchored = regularSessionBucket(ts, period, regularSession.timezone)
    if (anchored != null) return anchored
  }
  if (period.timespan === 'minute') {
    const d = new Date(ts)
    const minute = d.getUTCMinutes()
    const aligned = minute - (minute % period.multiplier)
    d.setUTCMinutes(aligned, 0, 0)
    return d.getTime()
  }
  if (period.timespan === 'hour') {
    const d = new Date(ts)
    const hour = d.getUTCHours()
    const aligned = hour - (hour % period.multiplier)
    d.setUTCHours(aligned, 0, 0, 0)
    return d.getTime()
  }
  if (period.timespan === 'day') {
    const d = new Date(ts)
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0)
  }
  if (period.timespan === 'week') {
    // Walk back to Monday at UTC midnight. JS `getUTCDay` returns 0=Sun..6=Sat;
    // Monday=1. Days-to-subtract = (day + 6) % 7 (so Mon → 0, Tue → 1 ... Sun → 6).
    const d = new Date(ts)
    const day = d.getUTCDay()
    const back = (day + 6) % 7
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - back, 0, 0, 0)
  }
  if (period.timespan === 'month') {
    const d = new Date(ts)
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0)
  }
  // year
  const d = new Date(ts)
  return Date.UTC(d.getUTCFullYear(), 0, 1, 0, 0, 0)
}

/** Bucketing for a chart that shows the regular session only. */
export interface RegularSessionBuckets {
  /** The exchange timezone the 09:30 open is read in. */
  timezone: string
}

function intradayMinutes(period: Period): number | null {
  if (period.timespan === 'minute') return period.multiplier
  if (period.timespan === 'hour') return period.multiplier * 60
  return null
}

/**
 * True when regular-session bars of `period` start on their clock boundaries:
 * intraday lengths that divide 09:30 (1m-30m). 1H and 4H do not, so their
 * regular-session bars start at the open instead, and an all-session bar set
 * cannot be filtered into them.
 */
export function tilesFromRegularOpen(period: Period): boolean {
  const minutes = intradayMinutes(period)
  return minutes != null && REGULAR_OPEN_MINUTES % minutes === 0
}

/**
 * Start of the regular-session bar holding `ts`, counted from the 09:30 open
 * (1H: 09:30, 10:30 ... 15:30; 4H: 09:30, 13:30). Null when `period` tiles from
 * the open anyway, or when `ts` is outside the regular session: those keep
 * their clock bucket, which the regular-session filter then drops.
 */
export function regularSessionBucket(ts: number, period: Period, timezone: string): number | null {
  const minutes = intradayMinutes(period)
  if (minutes == null || REGULAR_OPEN_MINUTES % minutes === 0) return null
  const { minutes: minuteOfDay, dateStr } = sessionClock(ts, timezone)
  if (minuteOfDay < REGULAR_OPEN_MINUTES || minuteOfDay >= regularCloseMinutesFor(dateStr)) {
    return null
  }
  const minuteStart = ts - (ts % 60_000)
  return minuteStart - ((minuteOfDay - REGULAR_OPEN_MINUTES) % minutes) * 60_000
}
